// src/components/antenna/antenna-dashboard.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle } from "lucide-react";
import { CommandPalette, useCommandPalette } from "../command-palette";
import { AIAssistantSidebar } from "../ai-assistant-sidebar";
import { ModelDetailsLoading } from "../model-details-loading";
import { openBrandMonitoringSocket } from "@/app/services/brand/brand-monitoring-ws";
import { buildWsUrl } from "@/api/url-route";
import { getStoredToken } from "@/api/api-fetch";
import { LIVE_MODEL_IDS, type ProviderId, isExcludedFromAnalytics, getModelLabel } from "@/app/constants/models";
import { fixActionsService } from "@/app/services/fix-actions/fix-actions-service";
import { toast } from "sonner";

import { providerLabel } from "@/app/utils/brand-helpers";
import { antennaCommands } from "../../data/antenna-metric-explanations";

import { AntennaHeader }               from "./antenna-header";
import { AntennaSnapshotAnalysis }      from "./antenna-snapshot-analysis";
import { AntennaPerceptionOverview }    from "./antenna-perception-overview";
import { AntennaBusinessMetrics }       from "./antenna-business-metrics";
import { AntennaBrandAssociations }     from "./antenna-brand-associations";
import { AskUpmodeBar }                 from "./ask-upmode-bar";
import { getStoredFirstName }           from "@/app/services/login/auth-service";
import { AntennaCompetitiveIntelligence } from "./antenna-competitive-intelligence";
import { AntennaRecommendedActions }    from "./antenna-recommended-actions";
import { BlueprintHandoffCard }         from "./blueprint-handoff-card";
import { fetchMonitoringHistory, fetchMonitoringRun, type MonitoringHistoryItem } from "@/app/services/brand/monitoring-history-api";

type ModelStatus = "queued" | "running" | "done" | "error";

interface AntennaDashboardProps {
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (collapsed: boolean) => void;
  brandName: string;
  selectedModels: ProviderId[];
  antennaData: any | null;
  onAntennaData: (data: any | null) => void;
  onLoadingChange?: (loading: boolean) => void;
  onNavigate?: (page: string, highlightActionTitle?: string) => void;
  /** When provided, the header shows a "Re-run" model picker (this brand only). */
  onRerun?: (models: string[]) => void;
}

export function AntennaDashboard({
  isSidebarCollapsed = false,
  onSidebarToggle,
  brandName,
  selectedModels,
  antennaData,
  onAntennaData,
  onLoadingChange,
  onNavigate,
  onRerun,
}: AntennaDashboardProps) {
  const [data, setData]                     = useState<any | null>(null);
  const [isLoading, setIsLoading]           = useState(true);
  const [isShowingSuccess, setIsShowingSuccess] = useState(false);
  const [errorMsg, setErrorMsg]             = useState<string | null>(null);

  // ── Run history (load previous monitoring runs) ──────────────────────────
  // `data` always holds the live/latest run. When the user picks an older run
  // from the header switcher we load it into `historicalData` and render that
  // instead, without disturbing the live load. `view` is what the UI shows.
  const [runHistory, setRunHistory]         = useState<MonitoringHistoryItem[]>([]);
  const [runHistoryLoading, setRunHistoryLoading] = useState(false);
  const [selectedRunId, setSelectedRunId]   = useState<number | null>(null);
  const [historicalData, setHistoricalData] = useState<any | null>(null);
  const view = historicalData ?? data;

  const [loadingProgress, setLoadingProgress]         = useState(0);
  const [currentLoadingModel, setCurrentLoadingModel] = useState<string>("");
  const [modelStates, setModelStates]                 = useState<Record<string, ModelStatus>>({});
  const [modelProgress, setModelProgress]             = useState<Record<string, number>>({});

  const [aiTopic, setAiTopic] = useState<string | undefined>(undefined);
  const { isOpen: isCommandPaletteOpen, setIsOpen: setCommandPaletteOpen } = useCommandPalette();

  const [isSnapshotExpanded,         setIsSnapshotExpanded]         = useState(true);
  const [isCompetitiveExpanded,      setIsCompetitiveExpanded]      = useState(true);
  const [isBrandAssociationsExpanded,setIsBrandAssociationsExpanded]= useState(true);
  const [isBusinessMetricsExpanded,  setIsBusinessMetricsExpanded]  = useState(true);

  const [perceptionModelFilter,    setPerceptionModelFilter]    = useState<string>("all");
  const [snapshotModelFilter,      setSnapshotModelFilter]      = useState<string>("all");
  const [businessMetricsModelFilter, setBusinessMetricsModelFilter] = useState<string>("all");
  const [brandAssocModelFilter,    setBrandAssocModelFilter]    = useState<string>("all");

  const [ingestedSourceIds, setIngestedSourceIds] = useState<Set<string>>(new Set());

  const wsRef  = useRef<WebSocket | null>(null);
  const token  = getStoredToken() ?? "";
  const brand  = brandName?.trim();
  const userFirstName = getStoredFirstName();

  const models = useMemo(
    () =>
      (selectedModels || []).filter(
        (m) => LIVE_MODEL_IDS.includes(m) && !isExcludedFromAnalytics(String(m)),
      ),
    [selectedModels],
  );

  const handleLoadingReady = () => {
    setIsLoading(false);
    setIsShowingSuccess(false);
  };

  const brandId = String(
    view?.runId ??
      view?.id ??
      (() => {
        try { return JSON.parse(localStorage.getItem("bm_last_result") || "{}")?.runId; }
        catch { return null; }
      })() ??
      localStorage.getItem("bm_brand_id") ??
      "default",
  );

  useEffect(() => {
    if (!brandId || brandId === "default") return;
    fixActionsService
      .getFixActionsPageData({ brandId })
      .then((pageData: any) => {
        const actionLog: any[] = Array.isArray(pageData?.actionLog) ? pageData.actionLog : [];
        const ids = new Set<string>();
        actionLog.forEach((entry) => {
          if (entry?.sourceModule === "antenna" && entry?.sourceId) ids.add(String(entry.sourceId));
        });
        setIngestedSourceIds(ids);
      })
      .catch(() => {});
  }, [brandId]);

  const handleImplementFromAntenna = async (action: any) => {
    try {
      const res = await fixActionsService.ingestActionLog({
        sourceModule: "antenna",
        sourceId:     String(action.id),
        title:        action.title,
        description:  action.description,
        priority:     action.priority,
        effort:       action.effort,
        impactScore:  Number(action.impact),
        brand:        view?.brandName || brand,
        modelsUsed:   (models || []).map((m) => String(m)),
        metadata: {
          from:          "antenna_recommended_actions",
          sourceIssue:   action.sourceIssue,
          whereToUpdate: action.whereToUpdate,
          updateDetails: action.updateDetails,
          references:    action.references,
        },
        brandId,
      });
      toast.success("Added to Action Log");
      setIngestedSourceIds((prev) => { const n = new Set(prev); n.add(String(action.id)); return n; });
      if (res?.deduped) toast.info("This action was already in your Action Log.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to add action");
    }
  };

  const handleViewInActionLog = (action: any) => onNavigate?.("fix-actions", action.title);

  const handleOpenAIChat = (topic?: string) => {
    setAiTopic(topic);
    if (isSidebarCollapsed && onSidebarToggle) onSidebarToggle(false);
  };

  useEffect(() => { onLoadingChange?.(isLoading || isShowingSuccess); }, [isLoading, isShowingSuccess, onLoadingChange]);
  useEffect(() => {
    if (!aiTopic) return;
    const t = setTimeout(() => setAiTopic(undefined), 500);
    return () => clearTimeout(t);
  }, [aiTopic]);

  useEffect(() => {
    if (!antennaData) return;
    setData(antennaData);
    setIsLoading(false);
    setErrorMsg(null);
  }, [antennaData]);

  // Id of the live/latest run currently held in `data`.
  const liveRunId: number | null = (() => {
    const raw = data?.runId ?? data?.id;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  })();

  // When a fresh live run loads (new runId), snap the view back to it and clear
  // any historical override.
  useEffect(() => {
    if (liveRunId != null) {
      setHistoricalData(null);
      setSelectedRunId(liveRunId);
    }
  }, [liveRunId]);

  // Load this brand's run history for the switcher. Refetches when a new live
  // run completes so the latest run shows up immediately.
  useEffect(() => {
    if (!brand) { setRunHistory([]); return; }
    let cancelled = false;
    setRunHistoryLoading(true);
    fetchMonitoringHistory(brand)
      .then((items) => { if (!cancelled) setRunHistory(items); })
      .catch(() => { if (!cancelled) setRunHistory([]); })
      .finally(() => { if (!cancelled) setRunHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [brand, liveRunId]);

  // Switcher selection: the live run shows `data`; any other run is fetched and
  // shown via `historicalData`.
  const handleSelectRun = async (id: number) => {
    setSelectedRunId(id);
    if (liveRunId != null && id === liveRunId) {
      setHistoricalData(null);
      return;
    }
    try {
      const details = await fetchMonitoringRun(id);
      const payload = details?.data && typeof details.data === "object" ? details.data : {};
      // runId + baselineSnapshot are stamped onto the live payload only AFTER it
      // is persisted, so a loaded run lacks them — restore from the run record.
      setHistoricalData({
        ...payload,
        runId: details.id,
        createdAt: details.createdAt,
        baselineSnapshot: payload.baselineSnapshot || details.createdAt,
      });
    } catch {
      toast.error("Could not load that run.");
      setSelectedRunId(liveRunId);
    }
  };

  // Always surface the live run in the switcher, even if the server list hasn't
  // caught up with it yet.
  const mergedRunHistory: MonitoringHistoryItem[] =
    liveRunId != null && !runHistory.some((r) => r.id === liveRunId)
      ? [
          { id: liveRunId, brandName: String(data?.brandName || brand || ""), createdAt: data?.createdAt ?? null, status: "completed" },
          ...runHistory,
        ]
      : runHistory;

  const seedModelMaps = (modelIds: ProviderId[]) => {
    const seededStates: Record<string, ModelStatus> = {};
    const seededProgress: Record<string, number>    = {};
    for (const m of modelIds) { seededStates[String(m)] = "queued"; seededProgress[String(m)] = 0; }
    setModelStates(seededStates);
    setModelProgress(seededProgress);
    setCurrentLoadingModel(modelIds.length ? providerLabel(String(modelIds[0])) : "");
    setLoadingProgress(0);
  };

  const recomputeOverallProgress = (progressMap: Record<string, number>) => {
    const values = Object.values(progressMap);
    if (!values.length) return 0;
    const avg = values.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0) / values.length;
    return Math.max(0, Math.min(100, avg));
  };

  useEffect(() => {
    let cancelled = false;
    if (antennaData) return;

    setIsLoading(true);
    setErrorMsg(null);
    setData(null);
    seedModelMaps(models);

    if (!token || !brand || models.length === 0) {
      setIsLoading(false);
      setErrorMsg(!token ? "Missing access token." : !brand ? "Missing brand name." : "No models selected.");
      return;
    }

    const socket = openBrandMonitoringSocket({
      baseWsUrl: buildWsUrl(),
      token, brand, models,
      onOpen: () => { if (cancelled) socket.close(); },
      onEvent: (evt) => {
        if (cancelled) return;
        if (evt.type === "analysis_started") seedModelMaps(models);
        if (evt.type === "model_status") {
          const engine = String(evt.engine ?? "");
          if (engine) {
            if (evt.status === "running") setCurrentLoadingModel(providerLabel(engine));
            setModelStates((prev) => ({ ...prev, [engine]: evt.status as ModelStatus }));
            if (evt.status === "done") {
              setModelProgress((prev) => {
                const next = { ...prev, [engine]: 100 };
                setLoadingProgress(recomputeOverallProgress(next));
                return next;
              });
            }
          }
        }
        if (evt.type === "progress") {
          const engine  = String((evt as any).engine ?? "");
          const percent = clampPercent((evt as any).percent);
          if (engine) {
            setCurrentLoadingModel(providerLabel(engine));
            setModelProgress((prev) => {
              const next = { ...prev, [engine]: percent };
              setLoadingProgress(recomputeOverallProgress(next));
              return next;
            });
          }
        }
        if (evt.type === "final_result") {
          setData(evt.data);
          onAntennaData(evt.data);
          setIsShowingSuccess(true);
        }
        if (evt.type === "error") {
          setIsLoading(false);
          setErrorMsg((evt as any).message || "Failed.");
        }
      },
      onError: () => { setIsLoading(false); setErrorMsg("WebSocket error."); },
      onClose: () => {},
    });

    wsRef.current = socket;
    return () => { cancelled = true; socket.close(); };
  }, [antennaData, brand, JSON.stringify(models)]);

  const handleCancel = () => { wsRef.current?.close(); setIsLoading(false); setErrorMsg("Cancelled by user."); };

  // Drop measurement-only lanes (e.g. gptweb) from every client-facing
  // per-model surface — scope chips, filter tabs, per-model perception. A run
  // sourced from the onboarding measurement battery keeps a gptweb per_model
  // entry, but it must not appear in the brand-monitoring view.
  const safeModelComparison = useMemo(
    () =>
      (Array.isArray(view?.modelComparison) ? view.modelComparison : []).filter(
        (m: any) => !isExcludedFromAnalytics(String(m?.model)),
      ),
    [view],
  );
  // Lanes the run planned but got nothing back from. Shown as failed, not
  // dropped: a missing lane must never read as one that was never asked.
  // Admins only; the API already withholds them from everyone else.
  const safeFailedModels = useMemo(() => {
    const isAdmin =
      localStorage.getItem("is_admin") === "true" ||
      sessionStorage.getItem("is_admin") === "true";
    if (!isAdmin) return [];
    return (Array.isArray(view?.failedModels) ? view.failedModels : []).filter(
      (f: any) => f?.model && !isExcludedFromAnalytics(String(f.model)),
    );
  }, [view]);
  const modelTabs = useMemo(
    () => safeModelComparison.map((m: any) => ({
      value: String(m.model),
      label: getModelLabel(String(m.model)),
    })),
    [safeModelComparison],
  );

  const loadingModels = useMemo(
    () => models.map((id) => ({
      id:       String(id),
      label:    providerLabel(String(id)),
      status:   modelStates[String(id)]  ?? "queued",
      progress: modelProgress[String(id)] ?? 0,
    })),
    [models, modelStates, modelProgress],
  );

  const snapshotByModel      = (view?.snapshotAnalysisByModel  ?? {}) as Record<string, any>;
  const perceptionByModel    = (view?.perceptionDataByModel    ?? {}) as Record<string, any>;
  const heroMetricsByModel   = (view?.heroMetricsByModel       ?? {}) as Record<string, any>;

  if (isLoading || isShowingSuccess) {
    return (
      <ModelDetailsLoading
        searchQuery={brand || "brand"}
        loadingProgress={loadingProgress}
        currentLoadingModel={currentLoadingModel}
        loadingModels={loadingModels}
        onCancel={handleCancel}
        onReadyNavigate={handleLoadingReady}
        isShowingSuccess={isShowingSuccess}
      />
    );
  }

  if (errorMsg || !view) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-8 h-8 mx-auto mb-3 text-infrared-coral" />
          <p className="text-infrared-coral text-sm">{errorMsg || "Failed to load data"}</p>
        </div>
      </div>
    );
  }

  const perceptionData       = view?.perceptionData         || {};
  const heroMetrics          = view?.heroMetrics            || {};
  const snapshotAnalysisData = view?.snapshotAnalysis       || null;
  const baselineSnapshotText = view?.baselineSnapshot       || "—";
  const recommendedActions   = Array.isArray(view?.recommendedActions) ? view.recommendedActions : [];

  const competitivePosition      = Array.isArray(view?.competitivePosition)      ? view.competitivePosition      : [];
  const competitivePositionLocal = Array.isArray(view?.competitivePositionLocal) ? view.competitivePositionLocal : [];
  const competitiveScopeRanks    = view?.competitiveScopeRanks ?? undefined;
  const competitiveVelocity      = view?.competitiveVelocity || { status: 0, rising: [], declining: [] };
  const dataIntegrity            = view?.dataIntegrity || { issues: [], modelsAffected: [], totalModels: 0 };

  // ── Brand's primary category — used to substitute [category] placeholders
  //    in the Business Metrics descriptions. The backend already produces this
  //    consistently across both the global and local ranking lists, so we
  //    read whichever is populated first.
  const brandCategory: string =
    (competitivePosition[0]?.category as string | undefined) ||
    (competitivePositionLocal[0]?.category as string | undefined) ||
    "";

  // Responsive right-margin: the AI sidebar is fixed-position so it overlays
  // content. Match the margin to the sidebar's actual rendered width so content
  // compresses instead of being covered. Sidebar is 380px open, ~44px collapsed.
  const contentMarginClass = isSidebarCollapsed
    ? "lg:mr-12"
    : "lg:mr-[380px]";

  return (
    <div className="flex min-h-screen bg-background">
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        commands={antennaCommands}
        onOpenAIChat={handleOpenAIChat}
      />

      <div className={`flex-1 overflow-auto transition-all duration-300 ${contentMarginClass}`}>
        <div className="w-full px-4 md:px-8">
          <AntennaHeader
            brandName={view?.brandName || brand}
            modelComparison={safeModelComparison}
            failedModels={safeFailedModels}
            baselineSnapshotText={baselineSnapshotText}
            runId={view?.runId ?? undefined}
            selectedModels={models.map(String)}
            onRerun={onRerun}
            runHistory={mergedRunHistory}
            selectedRunId={selectedRunId}
            onSelectRun={handleSelectRun}
            runHistoryLoading={runHistoryLoading}
          />

          <div className="px-2 md:px-8 py-6 md:py-8 flex flex-col gap-[var(--spacing-section-gap)]">

            {snapshotAnalysisData && (
              <AntennaSnapshotAnalysis
                data={snapshotAnalysisData}
                snapshotByModel={snapshotByModel}
                modelTabs={modelTabs}
                selectedModel={snapshotModelFilter}
                onModelChange={setSnapshotModelFilter}
                isExpanded={isSnapshotExpanded}
                toggleExpanded={() => setIsSnapshotExpanded(!isSnapshotExpanded)}
                onOpenAIChat={handleOpenAIChat}
                userName={userFirstName || undefined}
              />
            )}

            <AskUpmodeBar brandName={view?.brandName || brand} onAsk={handleOpenAIChat} />

            <AntennaPerceptionOverview
              perceptionData={perceptionData}
              perceptionDataByModel={perceptionByModel}
              heroMetrics={heroMetrics}
              heroMetricsByModel={heroMetricsByModel}
              modelComparison={safeModelComparison}
              failedModelCount={safeFailedModels.length}
              modelTabs={modelTabs}
              selectedFilter={perceptionModelFilter}
              onFilterChange={setPerceptionModelFilter}
              onOpenAIChat={handleOpenAIChat}
              baselineSnapshotText={baselineSnapshotText}
            />

            <AntennaBusinessMetrics
              heroMetrics={heroMetrics}
              heroMetricsByModel={heroMetricsByModel}
              perceptionData={perceptionData}
              perceptionDataByModel={perceptionByModel}
              themes={snapshotAnalysisData?.detectedThemes ?? []}
              themesByModel={snapshotByModel}
              dataIntegrity={dataIntegrity}
              modelTabs={modelTabs}
              selectedModel={businessMetricsModelFilter}
              onModelChange={setBusinessMetricsModelFilter}
              isExpanded={isBusinessMetricsExpanded}
              toggleExpanded={() => setIsBusinessMetricsExpanded(!isBusinessMetricsExpanded)}
              onOpenAIChat={handleOpenAIChat}
              category={brandCategory}
              brandName={brand}
              onNavigate={onNavigate}
            />

            <AntennaBrandAssociations
              perceptionData={perceptionData}
              perceptionDataByModel={perceptionByModel}
              themes={snapshotAnalysisData?.detectedThemes ?? []}
              themesByModel={snapshotByModel}
              modelTabs={modelTabs}
              selectedModel={brandAssocModelFilter}
              onModelChange={setBrandAssocModelFilter}
              isExpanded={isBrandAssociationsExpanded}
              toggleExpanded={() => setIsBrandAssociationsExpanded(!isBrandAssociationsExpanded)}
            />

            <AntennaCompetitiveIntelligence
              competitivePosition={competitivePosition}
              competitivePositionLocal={competitivePositionLocal}
              competitiveScopeRanks={competitiveScopeRanks}
              competitiveVelocity={competitiveVelocity}
              brandName={view?.brandName || brand}
              isExpanded={isCompetitiveExpanded}
              toggleExpanded={() => setIsCompetitiveExpanded(!isCompetitiveExpanded)}
              onOpenAIChat={handleOpenAIChat}
            />

            <BlueprintHandoffCard
              brandName={view?.brandName || brand}
              onOpenBlueprint={() => onNavigate?.("deep-signal", "blueprint")}
            />

            <AntennaRecommendedActions
              recommendedActions={recommendedActions}
              runId={view?.runId ?? undefined}
              brandName={view?.brandName || brand}
              selectedModels={models.map(String)}
              ingestedSourceIds={ingestedSourceIds}
              onImplementAction={handleImplementFromAntenna}
              onViewInActionLog={handleViewInActionLog}
            />
          </div>
        </div>
      </div>

      {/* AI Assistant — hidden below lg (< 1024px) */}
      <div className="hidden lg:block">
        <AIAssistantSidebar
          phase="antenna"
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={onSidebarToggle}
          initialTopic={aiTopic}
        />
      </div>
    </div>
  );
}

function clampPercent(v: any): number {
  const n = Number(v);
  return isNaN(n) ? 0 : Math.max(0, Math.min(100, n));
}